import {useState} from 'react'
import type {Sprint} from '../../types'
import {useApp} from '../../context/AppContext'
import Modal from '../common/Modal'
import {createSprint} from '../../services/api'
import {parseLocalDate} from '../../utils/helpers'

interface SprintFormProps {
  sprint?: Sprint
  onClose: () => void
  onSaved: (id: string) => void
}
function dateInput(date: Date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

export default function SprintForm({sprint, onClose, onSaved}: SprintFormProps) {
  const {state, dispatch, apiUpdateSprint} = useApp()
  const [name, setName] = useState(sprint?.name || '')
  const [goal, setGoal] = useState(sprint?.goal || '')
  const [boardId, setBoardId] = useState(
    sprint?.boardId || state.currentBoardId || state.boards[0]?.id || ''
  )
  const initialStartDate = sprint?.startDate
    ? dateInput(parseLocalDate(sprint.startDate))
    : dateInput(new Date())
  const initialEndDate = sprint?.endDate
    ? dateInput(parseLocalDate(sprint.endDate))
    : dateInput(new Date(Date.now() + 14 * 86400000))
  const [startDate, setStartDate] = useState(initialStartDate)
  const [endDate, setEndDate] = useState(initialEndDate)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const invalidDates = !!startDate && !!endDate && endDate < startDate
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (submitting || !name.trim() || !boardId || !startDate || !endDate || invalidDates) return
    setSubmitting(true)
    setError('')
    try {
      const values = {
        name: name.trim(),
        goal: goal.trim(),
        boardId,
        startDate: startDate + 'T00:00:00',
        endDate: endDate + 'T23:59:59',
      }
      if (sprint) {
        await apiUpdateSprint({
          id: sprint.id,
          name: values.name,
          goal: values.goal,
          ...(startDate !== initialStartDate ? {startDate: values.startDate} : {}),
          ...(endDate !== initialEndDate ? {endDate: values.endDate} : {}),
        })
        onSaved(sprint.id)
      } else {
        const created = await createSprint({...values, status: 'planning', taskIds: []})
        dispatch({type: 'ADD_SPRINT', payload: created})
        onSaved(created.id)
      }
      onClose()
    } catch {
      setError('Não foi possível salvar o sprint. Revise os dados e tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      closeDisabled={submitting}
      title={sprint ? 'Editar sprint' : 'Novo sprint'}
      description='Defina o objetivo, o quadro e o período do ciclo.'
      size='lg'
    >
      <form onSubmit={handleSubmit} className='p-5 md:p-6 space-y-5'>
        {error && (
          <p role='alert' className='p-3 rounded-xl bg-red-50 text-sm text-red-600'>
            {error}
          </p>
        )}
        <fieldset disabled={submitting} className='space-y-4'>
          <div>
            <label htmlFor='sprint-name' className='block text-sm font-medium text-slate-700 mb-1'>
              Nome do sprint *
            </label>
            <input
              id='sprint-name'
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              maxLength={120}
              autoFocus
              placeholder='Ex.: Sprint 12 — Experiência do cliente'
              className='w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm'
            />
          </div>
          <div>
            <label htmlFor='sprint-goal' className='block text-sm font-medium text-slate-700 mb-1'>
              Objetivo do ciclo
            </label>
            <textarea
              id='sprint-goal'
              value={goal}
              onChange={(event) => setGoal(event.target.value)}
              rows={3}
              placeholder='Qual resultado a equipe pretende alcançar?'
              className='w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm resize-y'
            />
          </div>
          <div>
            <label htmlFor='sprint-board' className='block text-sm font-medium text-slate-700 mb-1'>
              Quadro *
            </label>
            <select
              id='sprint-board'
              value={boardId}
              onChange={(event) => setBoardId(event.target.value)}
              required
              disabled={!!sprint}
              className='w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm disabled:bg-slate-50'
            >
              <option value=''>Selecione um quadro</option>
              {state.boards.map((board) => (
                <option key={board.id} value={board.id}>
                  {board.name}
                </option>
              ))}
            </select>
            <p className='text-xs text-slate-500 mt-1.5'>
              As tarefas disponíveis para este sprint pertencem a este quadro.
            </p>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            <div>
              <label
                htmlFor='sprint-start'
                className='block text-sm font-medium text-slate-700 mb-1'
              >
                Início *
              </label>
              <input
                id='sprint-start'
                type='date'
                required
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className='w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm'
              />
            </div>
            <div>
              <label htmlFor='sprint-end' className='block text-sm font-medium text-slate-700 mb-1'>
                Término *
              </label>
              <input
                id='sprint-end'
                type='date'
                required
                min={startDate}
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                aria-invalid={invalidDates}
                className='w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm'
              />
            </div>
          </div>
          {invalidDates && (
            <p role='alert' className='text-sm text-red-600'>
              O término deve ser igual ou posterior à data de início.
            </p>
          )}
          {!sprint && (
            <p className='p-3 bg-brand-50 rounded-xl text-sm text-brand-700'>
              O sprint será criado em planejamento. Adicione as tarefas e inicie o ciclo quando a
              equipe estiver pronta.
            </p>
          )}
        </fieldset>
        <div className='flex justify-end gap-3 pt-4 border-t border-slate-100'>
          <button
            type='button'
            disabled={submitting}
            onClick={onClose}
            className='devops-secondary-button'
          >
            Cancelar
          </button>
          <button
            type='submit'
            disabled={
              submitting || !name.trim() || !boardId || !startDate || !endDate || invalidDates
            }
            className='devops-primary-button disabled:opacity-50'
          >
            {submitting ? 'Salvando...' : sprint ? 'Salvar alterações' : 'Criar sprint'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
